import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { AuthProviderStatus, AuthStatusResponse, AuthUser, UserWorkspacePayload } from '@ukrop-nm/shared';
import { diagramStore } from '../state/diagramStore';
import { emitAccountChanged, getAccountOwner, setAccountOwner, SETTINGS_CHANGED_EVENT } from './accountScope';
import { applyLocalWorkspace, collectAllLocalWorkspaces, collectLocalWorkspace, compactWorkspaceForCloud } from './workspaceLocal';
import { mergeWorkspaces } from './workspaceMerge';

export type WorkspaceSyncState = 'idle' | 'loading' | 'saving' | 'saved' | 'dirty' | 'error';

type PersistOpts = { immediate?: boolean; localOnly?: boolean };

/** Abort hung Neon PUTs so the save badge cannot stay on «Збереження…» forever. */
const WORKSPACE_PUT_TIMEOUT_MS = 30_000;

function persistErrorMessage(err: unknown): string {
  if (err instanceof DOMException && (err.name === 'TimeoutError' || err.name === 'AbortError')) {
    return 'timeout';
  }
  if (err instanceof Error && (err.name === 'TimeoutError' || err.name === 'AbortError')) {
    return 'timeout';
  }
  return err instanceof Error ? err.message : 'network';
}

type AuthContextValue = {
  ready: boolean;
  user: AuthUser | null;
  providers: AuthProviderStatus;
  guestAllowed: boolean;
  passwordLogin: boolean;
  registrationOpen: boolean;
  oauthAllowed: boolean;
  syncState: WorkspaceSyncState;
  syncError: string | null;
  loginUrl: (provider: keyof AuthProviderStatus, opts?: { remember?: boolean; hint?: string; next?: string }) => string;
  setSessionUser: (user: AuthUser | null) => Promise<void>;
  logout: () => Promise<void>;
  persistWorkspace: (opts?: PersistOpts) => Promise<boolean>;
  refreshWorkspace: () => Promise<boolean>;
};

const emptyProviders: AuthProviderStatus = {
  google: false,
  facebook: false,
  github: false,
  microsoft: false,
};

const AuthContext = createContext<AuthContextValue | null>(null);

function applyUser(user: AuthUser | null) {
  setAccountOwner(user?.id ?? null);
  diagramStore.reloadFromStorage({ force: true });
  emitAccountChanged();
}

function cloudHasDiagrams(payload: UserWorkspacePayload | null | undefined): boolean {
  return Boolean(payload?.diagrams && Object.keys(payload.diagrams).length);
}

async function fetchCloudWorkspace(): Promise<UserWorkspacePayload | null> {
  const cloud = await fetch('/api/me/workspace', { credentials: 'include' });
  if (!cloud.ok) throw new Error(`workspace-get-${cloud.status}`);
  const body = (await cloud.json()) as { payload: UserWorkspacePayload | null };
  return body.payload;
}

function workspaceNeedsUpload(
  local: UserWorkspacePayload,
  cloud: UserWorkspacePayload | null | undefined,
): boolean {
  const localCustom = Object.entries(local.diagrams ?? {}).filter(
    ([id, bundle]) => id !== 'SITE-OFFICE-3F' && Boolean(bundle?.document),
  );
  if (!cloudHasDiagrams(cloud)) return localCustom.length > 0;
  for (const [id, bundle] of localCustom) {
    const remote = cloud?.diagrams?.[id]?.document;
    if (!remote) return true;
    const localTs =
      bundle.document && typeof bundle.document === 'object' && 'updatedAt' in bundle.document
        ? String((bundle.document as { updatedAt?: unknown }).updatedAt ?? '')
        : '';
    const remoteTs =
      remote && typeof remote === 'object' && 'updatedAt' in remote
        ? String((remote as { updatedAt?: unknown }).updatedAt ?? '')
        : '';
    if (localTs && remoteTs && localTs > remoteTs) return true;
    if (localTs && !remoteTs) return true;
  }
  return (local.deletedSiteIds ?? []).some((id) => Boolean(cloud?.diagrams?.[id]));
}

async function putWorkspace(
  payload: UserWorkspacePayload,
  signal: AbortSignal,
): Promise<{ ok: boolean; error?: string }> {
  const lite = compactWorkspaceForCloud(payload);
  const stripped = compactWorkspaceForCloud(payload, { stripPhotos: true });
  const minimal = compactWorkspaceForCloud(payload, { stripPhotos: true, versionLimit: 0 });
  const attempts: UserWorkspacePayload[] =
    JSON.stringify(lite).length > 400_000 ? [stripped, minimal] : [lite, stripped, minimal];
  let lastError = 'save-failed';
  for (const body of attempts) {
    if (signal.aborted) return { ok: false, error: 'timeout' };
    try {
      const res = await fetch('/api/me/workspace', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal,
      });
      const json = (await res.json().catch(() => ({}))) as {
        persisted?: boolean;
        reason?: string;
        error?: string;
        detail?: string;
      };
      if (res.status === 413 || json.error === 'too-large') {
        lastError = 'too-large';
        continue;
      }
      if (!res.ok) {
        lastError = json.detail || json.error || `http-${res.status}`;
        continue;
      }
      if (json.persisted === false) {
        lastError = json.reason || 'no-storage';
        continue;
      }
      return { ok: true };
    } catch (err) {
      lastError = persistErrorMessage(err);
    }
  }
  return { ok: false, error: lastError };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [providers, setProviders] = useState<AuthProviderStatus>(emptyProviders);
  const [guestAllowed, setGuestAllowed] = useState(false);
  const [passwordLogin, setPasswordLogin] = useState(false);
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [oauthAllowed, setOauthAllowed] = useState(false);
  const [syncState, setSyncState] = useState<WorkspaceSyncState>('idle');
  const [syncError, setSyncError] = useState<string | null>(null);
  const userRef = useRef<AuthUser | null>(null);
  const syncReady = useRef(false);
  const syncInFlight = useRef<Promise<boolean> | null>(null);
  const persistInFlight = useRef<Promise<boolean> | null>(null);
  const persistAbortRef = useRef<AbortController | null>(null);
  const lastPutFingerprint = useRef<string | null>(null);
  const persistWorkspaceRef = useRef<((opts?: PersistOpts) => Promise<boolean>) | null>(null);

  const hydrateFromServer = useCallback(async (): Promise<boolean> => {
    if (!getAccountOwner()) return false;
    syncReady.current = false;
    diagramStore.setPersistHandler(null);
    setSyncState('loading');

    let cloud: UserWorkspacePayload | null = null;
    try {
      cloud = await fetchCloudWorkspace();
    } catch (err) {
      console.warn('[workspace] download failed', err);
      setSyncError(err instanceof Error ? err.message : 'download-failed');
      setSyncState('error');
      const local = collectAllLocalWorkspaces();
      applyLocalWorkspace(local);
      diagramStore.reloadFromStorage({ force: true });
      emitAccountChanged();
      syncReady.current = true;
      if (persistWorkspaceRef.current) {
        diagramStore.setPersistHandler(persistWorkspaceRef.current);
      }
      return false;
    }

    const local = cloudHasDiagrams(cloud)
      ? collectLocalWorkspace()
      : collectAllLocalWorkspaces();
    const merged = mergeWorkspaces(local, cloud);
    applyLocalWorkspace(merged);
    diagramStore.reloadFromStorage({ force: true });
    emitAccountChanged();

    const dirty = workspaceNeedsUpload(merged, cloud);
    lastPutFingerprint.current = dirty ? null : JSON.stringify(compactWorkspaceForCloud(merged));
    setSyncError(null);
    setSyncState(dirty ? 'dirty' : 'saved');
    syncReady.current = true;
    if (persistWorkspaceRef.current) {
      diagramStore.setPersistHandler(persistWorkspaceRef.current);
    }
    return true;
  }, []);

  const persistWorkspace = useCallback(async (opts?: PersistOpts): Promise<boolean> => {
    if (opts?.localOnly) {
      setSyncState((cur) => (cur === 'saving' || cur === 'loading' ? cur : 'dirty'));
      return false;
    }
    if (!userRef.current || !syncReady.current) return false;

    persistAbortRef.current?.abort();
    const ac = new AbortController();
    persistAbortRef.current = ac;
    const timeoutId = window.setTimeout(() => ac.abort(), WORKSPACE_PUT_TIMEOUT_MS);

    const run = (async () => {
      setSyncState('saving');
      try {
        // Yield so Save modal / header can paint before the heavy stringify + PUT.
        await new Promise<void>((resolve) => {
          window.setTimeout(resolve, 0);
        });
        if (ac.signal.aborted) return false;
        const payload = collectLocalWorkspace();
        if (ac.signal.aborted) return false;
        const fingerprint = JSON.stringify(compactWorkspaceForCloud(payload));
        if (lastPutFingerprint.current === fingerprint) {
          setSyncError(null);
          setSyncState('saved');
          return true;
        }
        const result = await putWorkspace(payload, ac.signal);
        if (ac.signal.aborted) return false;
        if (result.ok) lastPutFingerprint.current = fingerprint;
        setSyncError(result.ok ? null : result.error ?? 'save-failed');
        setSyncState(result.ok ? 'saved' : 'error');
        return result.ok;
      } catch (err) {
        if (ac.signal.aborted) {
          if (persistAbortRef.current === ac) {
            setSyncError('timeout');
            setSyncState('error');
          }
          return false;
        }
        setSyncError(persistErrorMessage(err) === 'timeout' ? 'timeout' : err instanceof Error ? err.message : 'save-failed');
        setSyncState('error');
        return false;
      } finally {
        window.clearTimeout(timeoutId);
      }
    })();

    persistInFlight.current = run;
    try {
      return await run;
    } finally {
      if (persistInFlight.current === run) persistInFlight.current = null;
    }
  }, []);

  userRef.current = user;
  persistWorkspaceRef.current = persistWorkspace;

  const refreshWorkspace = useCallback(async () => {
    if (!user) return false;
    if (syncInFlight.current) return syncInFlight.current;
    const sync = hydrateFromServer();
    syncInFlight.current = sync;
    const ok = await sync;
    syncInFlight.current = null;
    return ok;
  }, [user, hydrateFromServer]);

  const setSessionUser = useCallback(
    async (next: AuthUser | null) => {
      setUser(next);
      applyUser(next);
      userRef.current = next;
      if (!next) {
        syncReady.current = false;
        lastPutFingerprint.current = null;
        diagramStore.setPersistHandler(null);
        setSyncState('idle');
        setSyncError(null);
        return;
      }
      if (syncInFlight.current) await syncInFlight.current;
      const sync = hydrateFromServer();
      syncInFlight.current = sync;
      await sync;
      syncInFlight.current = null;
      diagramStore.setPersistHandler(persistWorkspace);
    },
    [persistWorkspace, hydrateFromServer],
  );

  useEffect(() => {
    if (user && syncReady.current) {
      diagramStore.setPersistHandler(persistWorkspace);
    } else if (!user) {
      diagramStore.setPersistHandler(null);
    }
    return () => diagramStore.setPersistHandler(null);
  }, [user, persistWorkspace]);

  useEffect(() => {
    if (!user) return;
    const onSettings = () => void persistWorkspace({ localOnly: true });
    window.addEventListener(SETTINGS_CHANGED_EVENT, onSettings);
    return () => window.removeEventListener(SETTINGS_CHANGED_EVENT, onSettings);
  }, [user, persistWorkspace]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/auth/status', { credentials: 'include' });
        if (!res.ok) throw new Error('status');
        const data = (await res.json()) as AuthStatusResponse;
        if (cancelled) return;
        setProviders(data.providers ?? emptyProviders);
        setGuestAllowed(data.guestAllowed === true);
        setPasswordLogin(data.passwordLogin === true);
        setRegistrationOpen(data.registrationOpen === true);
        setOauthAllowed(data.oauthAllowed === true);
        setUser(data.user);
        applyUser(data.user);
        userRef.current = data.user;
        if (data.user) {
          if (syncInFlight.current) await syncInFlight.current;
          const sync = hydrateFromServer();
          syncInFlight.current = sync;
          await sync;
          syncInFlight.current = null;
          if (!cancelled) diagramStore.setPersistHandler(persistWorkspace);
        }
      } catch {
        if (!cancelled) {
          setUser(null);
          applyUser(null);
          userRef.current = null;
        }
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = useCallback(async () => {
    if (syncReady.current) {
      await persistWorkspace({ immediate: true });
    }
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    } catch {
      /* ignore */
    }
    syncReady.current = false;
    lastPutFingerprint.current = null;
    userRef.current = null;
    setUser(null);
    applyUser(null);
    setSyncState('idle');
    setSyncError(null);
  }, [persistWorkspace]);

  const loginUrl = useCallback(
    (
      provider: keyof AuthProviderStatus,
      opts?: { remember?: boolean; hint?: string; next?: string },
    ) => {
      const params = new URLSearchParams();
      if (opts?.remember) params.set('remember', '1');
      if (opts?.hint) params.set('hint', opts.hint);
      params.set('next', opts?.next || '/');
      return `/api/auth/login/${provider}?${params.toString()}`;
    },
    [],
  );

  const value = useMemo(
    () => ({
      ready,
      user,
      providers,
      guestAllowed,
      passwordLogin,
      registrationOpen,
      oauthAllowed,
      syncState,
      syncError,
      loginUrl,
      setSessionUser,
      logout,
      persistWorkspace,
      refreshWorkspace,
    }),
    [
      ready,
      user,
      providers,
      guestAllowed,
      passwordLogin,
      registrationOpen,
      oauthAllowed,
      syncState,
      syncError,
      loginUrl,
      setSessionUser,
      logout,
      persistWorkspace,
      refreshWorkspace,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
